import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, PlusCircle } from "@phosphor-icons/react";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { SectionWithHeader } from "../../shared/ui/SectionWithHeader";

import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { defaultIconProps } from "../../shared/ui/icons";
import { WalletCard } from "../components/WalletCard";
import { BillingHistoryList } from "../components/BillingHistoryList";
import { BillingTopUpModal } from "../components/BillingTopUpModal";
import { useBillingSummary } from "../hooks/useBillingSummary";
import { useBillingLedger } from "../hooks/useBillingLedger";
import { useTopUpModal } from "../hooks/useTopUpModal";
import { BILLING_TOPUP_SIGNAL_KEY } from "../constants";
import { useAuth } from "../../../providers/AuthProvider";

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const actionBarClasses = "flex flex-wrap items-center gap-3";
const summaryGridClasses =
  "grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-start";

export function BillingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => [{ label: t("billing.page.title") }], [t]);
  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    includeHome: true,
    fallback: { to: "/dashboard" },
  });

  const summary = useBillingSummary();
  const [ledgerParams] = useState({ limit: 25, offset: 0 });
  const ledger = useBillingLedger(ledgerParams);
  const topUpModal = useTopUpModal(25);

  const refreshSummary = summary.refresh;
  const refreshLedger = ledger.refresh;

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === BILLING_TOPUP_SIGNAL_KEY && event.newValue) {
        refreshSummary();
        refreshLedger();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [refreshLedger, refreshSummary]);

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={t("billing.page.title")}
        subtitle={t("billing.page.subtitle")}
        actions={
          <div className={actionBarClasses}>
            <button
              type="button"
              className="pm-btn pm-btn-primary pm-btn-md"
              onClick={() => topUpModal.open()}
            >
              <PlusCircle {...defaultIconProps} aria-hidden />
              {t("billing.card.top_up")}
            </button>
            <button
              type="button"
              className="pm-btn pm-btn-ghost pm-btn-md"
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      <div className={summaryGridClasses}>
        <WalletCard
          summary={summary.data}
          isLoading={summary.isLoading}
          error={summary.error}
          userName={user?.name ?? user?.email ?? undefined}
          onClick={() => topUpModal.open()}
        />
        <SectionWithHeader
          className="flex flex-col gap-4"
          title={t("billing.history.title")}
          description={t("billing.history.description")}
          actions={
            <button
              type="button"
              className="pm-btn pm-btn-ghost pm-btn-sm"
              onClick={ledger.refresh}
            >
              {t("billing.history.refresh")}
            </button>
          }
        >
          <BillingHistoryList
            entries={ledger.data}
            isLoading={ledger.isLoading}
            error={ledger.error}
          />
        </SectionWithHeader>
      </div>

      <BillingTopUpModal
        controller={topUpModal}
        onCompleted={summary.refresh}
      />
    </PageShell>
  );
}
